import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  Error as ErrorIcon,
  AccountBalance as BalanceIcon,
  Receipt as TransactionIcon,
  Add as AddIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { jobsApi, usersApi, paymentsApi, creditsApi } from '../services/api';
import { Job, AuthenticatedUserStats, UserCreditSettingsDto } from '../types';





const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<AuthenticatedUserStats | null>(null);
  const [creditSettings, setCreditSettings] = useState<UserCreditSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Purchase credits state
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [tokenCost, setTokenCost] = useState<number>(0.10); // Default fallback value
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const tokenCostResponse = await usersApi.getTokenCost();
      setTokenCost(tokenCostResponse.data.costPerToken);

      const recentJobsResponse = await jobsApi.getUserJobs(1, 3);
      setJobs(recentJobsResponse.data);

      const userStats = await usersApi.getAuthenticatedUserStats();
      setStats(userStats.data);

      const creditSettingsResponse = await creditsApi.getCreditSettings();
      setCreditSettings(creditSettingsResponse.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await jobsApi.uploadAudio(file);
      setUploadSuccessMessage(true);
      await fetchUserData();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePurchaseCredits = async () => {
    if (!purchaseAmount || isNaN(Number(purchaseAmount)) || Number(purchaseAmount) <= 0) {
      return;
    }

    const purchaseAmountNum = Number(purchaseAmount);
    
    // Check purchase limit
    if (creditSettings?.maxCreditPurchaseAmount && purchaseAmountNum > creditSettings.maxCreditPurchaseAmount) {
      setError(`Maximum purchase limit is ${creditSettings.maxCreditPurchaseAmount} credits per transaction.`);
      return;
    }

    // Check balance limit
    const currentBalance = stats?.balance || 0;
    const newBalance = currentBalance + purchaseAmountNum;
    if (creditSettings?.maxCreditLimit && newBalance > creditSettings.maxCreditLimit) {
      setError(`This purchase would exceed your maximum balance limit of ${creditSettings.maxCreditLimit} credits. Your current balance is ${currentBalance} credits. Please reduce the purchase amount.`);
      return;
    }

    try {
      setPurchaseLoading(true);
      setError(null); // Clear any previous errors
      
      // Call the backend to create a Stripe checkout session
      const response = await paymentsApi.createCheckoutSession(purchaseAmountNum);
      
      // Redirect to Stripe's payment page
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received from server');
      }
      
    } catch (err) {
      console.error('Purchase failed:', err);
      setError('Failed to initiate payment. Please try again.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon color="success" />;
      case 'processing':
        return <PlayIcon color="primary" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    const totalMinutes = Math.floor(seconds / 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">
          Welcome back, {user?.name}!
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setPurchaseDialogOpen(true)}
          sx={{ minWidth: 'fit-content' }}
        >
          Purchase Credits
        </Button>
      </Box>
      
      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <PlayIcon color="primary" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Jobs
                </Typography>
                <Typography variant="h4">
                  {stats?.totalJobs || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <CompletedIcon color="success" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4">
                  {stats?.completedJobs || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <BalanceIcon color="primary" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Average Duration
                </Typography>
                <Typography variant="h4">
                  {stats ? formatDuration(stats.averageDuration) : '0s'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <TransactionIcon color="secondary" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Credit Balance
                </Typography>
                <Typography variant="h4">
                  {stats?.balance || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Recent Jobs */}
      <Card>
        <CardContent>
          <input
            type="file"
            ref={fileInputRef}
            accept=".mp3,audio/mpeg"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">Recent Jobs</Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? 'Uploading…' : 'Upload recording'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/user/jobs')}
              >
                View All Jobs
              </Button>
            </Box>
          </Box>
          
          <List>
          {jobs.slice(0, 5).map((job) => (
            <ListItem 
              key={job.id} 
              divider
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 0 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <ListItemIcon sx={{ minWidth: { xs: 40, sm: 56 } }}>
                  {getStatusIcon(job.status)}
                </ListItemIcon>
                <ListItemText
                  primary={job.filename}
                  secondary={
                    <Box>
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{ 
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        Created: {new Date(job.uploadedAt).toLocaleDateString()}
                        {job.duration && ` • Duration: ${formatDuration(job.duration)}`}
                        {job.bookmarkCount && ` • Bookmarks: ${job.bookmarkCount}`}
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    minWidth: 0,
                    flex: 1,
                    '& .MuiListItemText-primary': {
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                />
                <Chip 
                  label={job.status} 
                  color={getStatusColor(job.status) as any}
                  size="small"
                  sx={{ 
                    flexShrink: 0,
                    ml: { xs: 0, sm: 1 },
                    mt: { xs: 1, sm: 0 }
                  }}
                />
              </Box>
            </ListItem>
          ))}
        </List>
          
          {jobs.length === 0 && (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No jobs found. Start by uploading your first recording!
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Purchase Credits Dialog */}
      <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)}>
        <DialogTitle>Purchase Credits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter the number of credits you would like to purchase. Credits are used for transcription and bookmark generation services.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Credits"
            type="number"
            fullWidth
            variant="outlined"
            value={purchaseAmount}
            onChange={(e) => {
              const value = e.target.value;
              setPurchaseAmount(value);
              // Clear error when user starts typing
              if (error) setError(null);
            }}
            inputProps={{ 
              min: 1,
              max: creditSettings?.maxCreditPurchaseAmount 
            }}
            error={!!error && (error.includes('exceed') || error.includes('Maximum purchase'))}
            helperText={
              creditSettings?.maxCreditPurchaseAmount 
                ? `Maximum: ${creditSettings.maxCreditPurchaseAmount} credits per transaction` 
                : undefined
            }
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Estimated cost: £{purchaseAmount ? (Number(purchaseAmount) * tokenCost).toFixed(2) : '0.00'} (£{tokenCost.toFixed(2)} per credit)
          </Typography>
          {creditSettings?.maxCreditLimit && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              Maximum account balance: {creditSettings.maxCreditLimit} credits (Current: {stats?.balance || 0} credits)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handlePurchaseCredits}
            variant="contained"
            disabled={!purchaseAmount || isNaN(Number(purchaseAmount)) || Number(purchaseAmount) <= 0 || purchaseLoading}
          >
            {purchaseLoading ? <CircularProgress size={20} /> : 'Purchase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        message="Payment successful! Your credits have been added to your account."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* Upload success Snackbar */}
      <Snackbar
        open={uploadSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setUploadSuccessMessage(false)}
        message="Recording uploaded; transcription started."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* Error Message Snackbar */}
      <Snackbar
        open={!!error && !error.includes('Failed to load')}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default UserDashboard; 